import React from 'react';
import { useProjects, useProject } from '../../hooks/queries/useProjects';
import { useProjectUIStore } from '../../store/projectUIStore';
import { ProjectList } from '../projects/ProjectList';
import { useParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { CollapsedSidebar } from './CollapsedSidebar';
import { useViewport } from '../../contexts/ViewportContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTopicList } from '../../contexts/TopicListContext';
import { logger } from '../../utils/logger';
import { useStatusBarInfo } from '../../hooks/useStatusBarInfo';
import { usePreloadCollaborators } from '../../hooks/queries/useCollaborators';
import { useQueryClient } from '@tanstack/react-query';
import { topicKeys } from '../../hooks/queries/topicKeys';
import { useTopicUIStore } from '../../store/topicUIStore';

interface SidebarProps {
  onProjectSelect: (projectId: string) => void;
}

export function Sidebar({ onProjectSelect }: SidebarProps) {
  const { projectId } = useParams();
  const { setActiveProject } = useProjectUIStore();
  useProjects(); // Called to ensure project data is fresh
  const { data: activeProject } = useProject(projectId);
  const queryClient = useQueryClient();
  const { setActiveTopicId } = useTopicUIStore();

  // Use sidebar state from context
  const { isCollapsed, expandSidebar, collapseSidebar } = useSidebar();
  // Get topic list collapsed state and functions from context
  const { isCollapsed: isTopicListCollapsed, collapseTopicList } = useTopicList();

  // Note: Collaborator subscriptions are now handled globally by RealtimeQueryProvider

  // Get the preload collaborators function from React Query
  const { preload: preloadCollaborators } = usePreloadCollaborators();

  const handleProjectSelect = async (id: string) => {
    // If the project is already selected, do nothing to prevent unnecessary reloading
    if (id === projectId) {
      return;
    }

    // Reset active topic ID using the topicUIStore
    setActiveTopicId(null);

    // Prefetch topics using React Query
    logger.component('Sidebar: Pre-fetching topics for new project', {
      projectId: id,
    });

    // Prefetch topics for the new project
    queryClient.prefetchQuery({
      queryKey: topicKeys.list({ projectId: id }),
      staleTime: 0, // Ensure fresh data
    });

    // Preload collaborators for the project (this will cache them for all topics)
    logger.component('Sidebar: Pre-loading project collaborators', {
      projectId: id,
    });

    // Non-blocking preload of collaborators with React Query
    preloadCollaborators(id);

    // Then set active project and navigate
    await setActiveProject(id);
    onProjectSelect(id);
  };

  // Handler to collapse both sidebar and topic list
  const handleSidebarCollapse = () => {
    logger.component('Sidebar: Collapsing sidebar and topic list');
    // First collapse the topic list
    collapseTopicList();
    // Then collapse the sidebar
    collapseSidebar();
  };

  // Get viewport state
  const { isMobile } = useViewport();

  // Get status bar information
  const { isNativeApp, statusBarHeight } = useStatusBarInfo();

  // Log status bar information for debugging
  React.useEffect(() => {
    if (isNativeApp && isMobile) {
      logger.capacitor('Sidebar: Using status bar height', {
        statusBarHeight,
      });
    }
  }, [isNativeApp, statusBarHeight, isMobile]);

  // Set CSS variable for sidebar width - update when collapsed state or viewport changes
  // Listen for the toggle-sidebars event to handle keyboard shortcut
  React.useEffect(() => {
    const handleToggleSidebars = () => {
      logger.component('Sidebar: Toggling sidebar from keyboard shortcut');
      if (isCollapsed) {
        expandSidebar();
      } else {
        collapseSidebar();
      }
    };

    document.addEventListener('toggle-sidebars', handleToggleSidebars);

    return () => {
      document.removeEventListener('toggle-sidebars', handleToggleSidebars);
    };
  }, [isCollapsed, expandSidebar, collapseSidebar]);

  React.useEffect(() => {
    // On mobile, set the available width to 100vw by setting sidebar width to 0
    if (isMobile) {
      document.documentElement.style.setProperty('--sidebar-width', '0px');
      document.documentElement.style.setProperty('--topic-list-width', '0px');
      document.documentElement.style.setProperty('--sidebar-total-width', '0px');
    } else {
      // On desktop, calculate based on collapsed state
      document.documentElement.style.setProperty('--sidebar-width', isCollapsed ? '50px' : '280px');

      // Set topic list width based on its collapsed state
      document.documentElement.style.setProperty(
        '--topic-list-width',
        isTopicListCollapsed ? '56px' : '280px'
      );

      // Calculate the total available width - dynamically based on both sidebar and topic list states
      const sidebarWidth = isCollapsed ? 50 : 280;
      const topicListWidth = isTopicListCollapsed ? 56 : 280;
      const totalWidth = `${sidebarWidth + topicListWidth}px`;
      document.documentElement.style.setProperty('--sidebar-total-width', totalWidth);
    }

    return () => {
      // Clean up when component unmounts
      document.documentElement.style.removeProperty('--sidebar-width');
      document.documentElement.style.removeProperty('--topic-list-width');
      document.documentElement.style.removeProperty('--sidebar-total-width');
    };
  }, [isCollapsed, isTopicListCollapsed, isMobile]);

  return (
    <div
      className={cn(
        'h-screen bg-[var(--sidebar-bg-color)] flex-shrink-0 transition-all duration-300',
        'hidden lg:block', // Hide on mobile/tablet, show on desktop
        'relative z-30', // Add z-index to ensure position in layout
        isCollapsed ? 'w-[50px]' : 'w-[280px]',
        'text-gray-900' // Update text color to black/very dark gray
      )}
    >
      {isCollapsed ? (
        <CollapsedSidebar
          project={activeProject || null}
          onExpand={expandSidebar}
          onProjectSelect={handleProjectSelect}
        />
      ) : (
        <div className="h-full">
          <ProjectList
            activeProjectId={projectId || null}
            onProjectSelect={handleProjectSelect}
            onCollapse={handleSidebarCollapse}
          />
        </div>
      )}
    </div>
  );
}
